import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { PRODUCT_RELATIONS_QUERY } from './products.gql';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';

interface ProductRelationsDialogProps {
  product_id: string;
  product_name: string;
  onClose: () => void;
}

interface ProductRelationsResponse {
  product: {
    id: string;
    code: string;
    name: string;
    partial_kind: string;
    parent_product_id: string | null;
    parent_factor: string | null;
    requires_current_weight: boolean;
    derived_stock_qty: string;
    part_of_kits: { id: string; code: string; name: string }[];
    part_of_services: { id: string; code: string; name: string }[];
    dose_children: {
      id: string;
      code: string;
      name: string;
      parent_factor: string | null;
      derived_stock_qty: string;
    }[];
  };
}

/**
 * Read-only relations summary for a catalog product:
 *   - "Forma parte de" — kits + services that consume this product.
 *   - "Dosis hijas" — dose-children when this product is a parent envase.
 *   - "Requiere peso" badge.
 */
export function ProductRelationsDialog({
  product_id,
  product_name,
  onClose,
}: ProductRelationsDialogProps): JSX.Element {
  const { t } = useTranslation();
  const { data, loading, error } = useQuery<ProductRelationsResponse>(PRODUCT_RELATIONS_QUERY, {
    variables: { id: product_id },
    fetchPolicy: 'cache-and-network',
  });
  const p = data?.product;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="max-h-[88vh] w-full max-w-2xl overflow-auto">
        <CardHeader>
          <CardTitle>{product_name}</CardTitle>
          {p ? (
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="outline">{p.code}</Badge>
              {p.requires_current_weight ? (
                <Badge variant="outline" className="border-primary text-primary">
                  {t('catalog.relations.requires_weight')}
                </Badge>
              ) : null}
              {p.partial_kind === 'parent_with_doses' ? (
                <Badge variant="muted">{t('catalog.relations.dose_child_kind')}</Badge>
              ) : p.partial_kind === 'fractional_qty' ? (
                <Badge variant="muted">{t('catalog.relations.fractional_kind')}</Badge>
              ) : null}
            </div>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && !p ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : null}
          {error ? (
            <p className="text-sm text-destructive">{error.message}</p>
          ) : null}
          {p ? (
            <>
              <Section title={t('catalog.relations.part_of_kits')}>
                {p.part_of_kits.length === 0 ? (
                  <Empty t_key="catalog.relations.empty_kits" />
                ) : (
                  <ul className="space-y-1 text-sm">
                    {p.part_of_kits.map((k) => (
                      <li key={k.id} className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{k.code}</span>
                        <span>{k.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
              <Section title={t('catalog.relations.part_of_services')}>
                {p.part_of_services.length === 0 ? (
                  <Empty t_key="catalog.relations.empty_services" />
                ) : (
                  <ul className="space-y-1 text-sm">
                    {p.part_of_services.map((s) => (
                      <li key={s.id} className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{s.code}</span>
                        <span>{s.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
              <Section title={t('catalog.relations.dose_children')}>
                {p.dose_children.length === 0 ? (
                  <Empty t_key="catalog.relations.empty_doses" />
                ) : (
                  <ul className="space-y-1 text-sm">
                    {p.dose_children.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-2">
                        <span>
                          <span className="font-mono text-xs text-muted-foreground">{d.code}</span>
                          <span className="ml-2">{d.name}</span>
                        </span>
                        <span className="font-mono text-xs text-muted-foreground">
                          {t('catalog.relations.dose_summary', {
                            factor: d.parent_factor ?? '—',
                            available: d.derived_stock_qty,
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {children}
    </div>
  );
}

function Empty({ t_key }: { t_key: string }): JSX.Element {
  const { t } = useTranslation();
  return <p className="text-xs text-muted-foreground">{t(t_key)}</p>;
}
